import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Lock } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["name", "Driver name"], ["plate", "Plate"], ["companyName", "Company"], ["truckType", "Truck type"],
  ["trailerType", "Trailer type"], ["mcNumber", "MC #"], ["dotNumber", "DOT #"], ["licNumber", "License #"],
];

const valuesOf = (s) => {
  const c = s.customer || {};
  return Object.fromEntries(FIELDS.map(([k]) => [k, (["name", "plate"].includes(k) ? c[k] : s[k] || c[k]) || ""]));
};

export default function TicketDetailsForm({ session, locked, onSaved }) {
  const [form, setForm] = useState(null);
  const [orig, setOrig] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!session) return;
    const v = valuesOf(session);
    setForm(v);
    setOrig(v);
  }, [session]);

  if (!form) return <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-slate-500" />;

  const save = async () => {
    const changed = Object.fromEntries(Object.entries(form).filter(([k, v]) => v.trim() !== (orig[k] || "").trim() && (v.trim() || orig[k])));
    if (!Object.keys(changed).length) return toast.info("Nothing changed");
    if ("name" in changed && !changed.name.trim()) return toast.error("Driver name cannot be blank");
    setSaving(true);
    try {
      const { data } = await api.patch(`/sessions/${session.id}/details`, changed);
      toast.success(`Saved ${data.changes.length} change(s) — logged to audit`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {locked && (
        <p data-testid="ticket-locked-note" className="flex items-center gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 p-3 text-sm text-amber-300">
          <Lock className="h-4 w-4" /> This ticket is checked out and locked. Only the owner can change it.
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <Label className="text-xs text-slate-400">{label}</Label>
            <Input data-testid={`ticket-edit-${k}`} value={form[k]} disabled={locked} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="h-10 border-slate-700 bg-slate-950 disabled:cursor-not-allowed disabled:border-slate-800 disabled:bg-slate-900 disabled:text-slate-400 disabled:opacity-70" />
          </div>
        ))}
      </div>
      {!locked && (
        <>
          <p className="text-xs text-slate-500">Every change is stamped with your name and the time in the Audit tab.</p>
          <Button data-testid="ticket-edit-save" onClick={save} disabled={saving} className="h-10 gap-2 bg-[#A0148C] font-bold hover:bg-[#80106F]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </Button>
        </>
      )}
    </div>
  );
}
